/**
 * Shared layer constants for the canvas editor.
 *
 * This file holds the fixed vocabulary that layers, groups, and the
 * Fabric renderers all agree on: layer type names, origin points,
 * group layout modes, auto-layout defaults, and the property keys used
 * to read/write transform state on a layer.
 *
 * Stores, composables, and canvas services import from here instead of
 * repeating string literals, so a rename only needs to happen once.
 */

import type { LayerBase } from '@/types/layers/layer';

export const LAYER_TYPES = {
  TEXT: 'Text',
  IMAGE: 'Image',
  SHAPE: 'Shape',
  VIDEO: 'Video',
  ELEMENT: 'Element',
  AUDIO: 'Audio',
  BACKGROUND_GROUP: 'BackgroundGroup'
} as const;

export const ORIGIN_X = {
  LEFT: 'left',
  CENTER: 'center',
  RIGHT: 'right'
} as const;

export const ORIGIN_Y = {
  TOP: 'top',
  CENTER: 'center',
  BOTTOM: 'bottom'
} as const;

export const GROUP_MODES = {
  ENLARGE: 'enlarge',
  MASK: 'mask',
  AUTO: 'auto'
} as const;

export const ELEMENT_KINDS = {
  GROUP: 'group',
  FRAME: 'frame'
} as const;

/**
 * AUTO mode runtime defaults. Used as seed for new auto-groups and as
 * fallback in `readAutoLayoutOpts` when a stored opt is missing.
 */
export const AUTO_LAYOUT_DEFAULTS = {
  direction: 'horizontal',
  gap: 8,
  // Every side of a new frame's padding.
  padding: 8,
  crossAlign: 'center',
  // Forward-compat — v1 honors only the default value.
  justify: 'start',
  wrap: false,
  reverse: false
} as const;

export const LAYER_PROPS = {
  LEFT: 'left',
  TOP: 'top',
  SCALE_X: 'scaleX',
  SCALE_Y: 'scaleY',
  ANGLE: 'angle',
  FLIP_X: 'flipX',
  FLIP_Y: 'flipY',
  OPACITY: 'opacity',
  VISIBLE: 'visible',
  LOCKED: 'locked',
  MAPPED_ATTRIBUTES: 'mappedAttributes',
  TIMING: 'timing',
  ANIMATIONS: 'animations'
} as const;

/**
 * Pure transform properties (position, scale, rotation, flip).
 * `flipX/flipY` ride here so a scale-handle drag past pivot (which fabric
 * captures as negative scale, normalized to flip + positive scale by
 * handleObjectModified) survives rebuild for every layer type.
 */
export const TRANSFORM_KEYS = [
  LAYER_PROPS.LEFT,
  LAYER_PROPS.TOP,
  LAYER_PROPS.SCALE_X,
  LAYER_PROPS.SCALE_Y,
  LAYER_PROPS.ANGLE,
  LAYER_PROPS.FLIP_X,
  LAYER_PROPS.FLIP_Y
] as const satisfies readonly (keyof LayerBase)[];

/**
 * Base transform properties shared by all layer renderers (1:1 Fabric passthrough).
 * Note: `visible` is NOT included — getVisibilityFabricProps() owns it, so
 * hidden layers fully vanish (or ghost while the ad's peek mode is on).
 */
export const BASE_FABRIC_KEYS: readonly (keyof LayerBase)[] = [
  ...TRANSFORM_KEYS,
  LAYER_PROPS.OPACITY
];
